import { invalidateIntegrationQuery, usePostExternalIntegrationStartStageMutation } from '@Queries/index';
import { bs } from '@Styles/styles';
import * as Api from '@ViewModels';
import {
	EtlState,
	IBaseEtlMetadata,
	IEtlMetadata,
	IExtractEtlMetadata,
	ILoadMetadata,
	IPreProcessEtlMetadata,
	ITransformEtlMetadata,
} from '@ViewModels';
import { DisclosureIcon } from '@WebComponents/svgs/icons/DisclosureIcon';
import { SuccessCheckIcon } from '@WebComponents/svgs/icons/SuccessCheckIcon';
import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import etlExtract from '../../../../../../../web/assets/etl-extract.svg';
import etlLoad from '../../../../../../../web/assets/etl-load.svg';
import etlTransform from '../../../../../../../web/assets/etl-transform.svg';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownTrigger,
} from '../../../../../../../web/components-v2/Dropdown';
import { Button } from '../../../../../../../web/components/Button';
import { ETLSubConnector } from '../ETLVisuals';
import { styleSheet } from './styles';

interface IETLStageProps {
	label: string;
	metadata: IBaseEtlMetadata;
	stage: keyof IEtlMetadata;
	provider: Api.IntegrationProvider;
	impersonationContext?: Api.IImpersonationContext;
	anyStageInProgress: boolean;
	isPipelineSuspended: boolean;
	preProcessMetadata?: Api.IPreProcessEtlMetadata;
}

export const ETLStage: React.FC<IETLStageProps> = ({
	label,
	metadata,
	stage,
	provider,
	impersonationContext,
	anyStageInProgress,
	isPipelineSuspended,
	preProcessMetadata,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

	const getDisplayDate = () => {
		const date = metadata.lastFinished || metadata.lastStarted;
		if (!date) return 'N/A';
		return moment(date).format('MM/DD/YY h:mm A');
	};

	const refreshIntegration = () => {
		invalidateIntegrationQuery({
			impersonationContext,
			provider,
		});
	};

	const postExternalIntegrationStartStageMutation = usePostExternalIntegrationStartStageMutation({
		impersonationContext,
		onSuccess: () => refreshIntegration(),
	});

	const getStageIcon = () => {
		if (stage === 'extract') return etlExtract;
		if (stage === 'transform') return etlTransform;
		if (stage === 'load') return etlLoad;
	};
	const onStart = (forceAll = false) => {
		if (isPipelineSuspended) {
			toast.info(
				'The pipeline is currently suspended. Starting any stage of the ETL requires resolving the suspended state first.'
			);
			return;
		}

		postExternalIntegrationStartStageMutation.mutateAsync({
			etlStage: (stage[0].toUpperCase() + stage.slice(1)) as Api.EtlStage,
			integrationProvider: provider,
			forceAll,
		});
	};

	const getSubLabel = (
		stageMetadata: IExtractEtlMetadata | ITransformEtlMetadata | ILoadMetadata | IPreProcessEtlMetadata
	) => {
		if (stageMetadata.state === EtlState.InProgress) return `Processing...`;
		if (stageMetadata.state === EtlState.Idle) return `Idle`;
		if (stageMetadata.state === EtlState.Suspended) return `Suspended`;
		if (stageMetadata.state === EtlState.IdleWithError) return `Idle with Error`;
		if (stageMetadata.state === EtlState.SuspendedWithError) return `Suspended with Error`;
		return `Completed`;
	};

	const isDisabled = anyStageInProgress;

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.content)}>
				<div className={css(styleSheet.iconContainer)}>
					<img src={getStageIcon()} alt={label} />
				</div>
				<div className={css(styleSheet.textContainer)}>
					<h3 className={css(styleSheet.label)}>{label}</h3>
					<p className={css(styleSheet.subLabel)}>{getSubLabel(metadata)}</p>
					{stage === 'extract' && preProcessMetadata && (
						<div className={css(styleSheet.substep)}>
							<ETLSubConnector status={preProcessMetadata.state} />
							<div>
								<h3
									className={css(
										styleSheet.substepLabel,
										preProcessMetadata.state !== EtlState.InProgress && styleSheet.substepLabelIdle
									)}
								>
									Pre-processing
								</h3>
								<p className={css(styleSheet.substepStatus)}>{getSubLabel(preProcessMetadata)}</p>
							</div>
						</div>
					)}
				</div>
			</div>
			<footer className={css(styleSheet.footer)}>
				<div className={css(styleSheet.dateContainer)}>
					{metadata.lastFinished ? <SuccessCheckIcon /> : null}
					{metadata.lastFinished ? 'Last Run: ' : 'Started Date: '}
					<span className={css(bs.fontBold)}>{getDisplayDate()}</span>
				</div>
				{stage === 'extract' || stage === 'transform' ? (
					<Dropdown open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
						<DropdownTrigger className={css(styleSheet.buttonStyleTrigger)} disabled={isDisabled}>
							<span className={css(styleSheet.triggerText)}>
								{metadata.state === EtlState.InProgress ? 'Running' : 'Start'}
							</span>
							<DisclosureIcon
								className={css(styleSheet.triggerChevron, isDropdownOpen && styleSheet.triggerChevronRotated)}
								fillColor='white'
							/>
						</DropdownTrigger>
						<DropdownContent styles={[styleSheet.compactDropdownContent]}>
							<DropdownItem onClick={() => onStart()} disabled={isDisabled}>
								From Last Run
							</DropdownItem>
							<DropdownItem onClick={() => onStart(true)} disabled={isDisabled}>
								{stage === 'extract' ? 'Full Extract' : 'Full Transform'}
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
				) : (
					<Button
						kind='primary'
						size='small'
						onClick={() => onStart()}
						disabled={isDisabled}
						className={css(styleSheet.button)}
					>
						{metadata.state === EtlState.InProgress ? 'Running' : 'Start'}
					</Button>
				)}
			</footer>
		</div>
	);
};
